import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { GridSearchIcon } from "@mui/x-data-grid";
import { debounce } from "lodash";
import { v4 as uuid } from "uuid";

import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { setParticipantSearch } from "../../Redux/reducers/participantsSlice";

const SEARCH_FIELD_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
] as const;

const ParticipantFieldSearch = () => {
  const dispatch = useAppDispatch();
  const searchField = useAppSelector((state) => state.participants.searchField);
  const searchQuery = useAppSelector((state) => state.participants.searchQuery);

  const [searchId, setSearchId] = useState(uuid());
  const prevSearchQuery = useRef(searchQuery);

  const modifySearchTerm = useMemo(
    () =>
      debounce((val: string) => {
        dispatch(setParticipantSearch({ searchField, searchQuery: val }));
      }, 500),
    [dispatch, searchField]
  );

  useEffect(() => () => modifySearchTerm.cancel(), [modifySearchTerm]);

  useEffect(() => {
    if (prevSearchQuery.current && !searchQuery) {
      setSearchId(uuid());
    }
    prevSearchQuery.current = searchQuery;
  }, [searchQuery]);

  const handleFieldChange = (newField: string) => {
    modifySearchTerm.cancel();
    dispatch(setParticipantSearch({ searchField: newField, searchQuery: "" }));
    setSearchId(uuid());
  };

  const handleClearSearch = () => {
    modifySearchTerm.cancel();
    dispatch(setParticipantSearch({ searchField, searchQuery: "" }));
    setSearchId(uuid());
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "40%" }}>
      <Select
        value={searchField}
        onChange={(e) => handleFieldChange(e.target.value)}
        size="small"
        sx={{ minWidth: 140 }}
      >
        {SEARCH_FIELD_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <TextField
        key={searchId}
        placeholder="Search"
        sx={{ flex: 1 }}
        defaultValue={searchQuery}
        onChange={(e) => modifySearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <GridSearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchQuery ? (
            <InputAdornment position="end">
              <IconButton onClick={handleClearSearch}>
                <CloseRounded />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  );
};

export default ParticipantFieldSearch;
